/**
 * A table whose columns carry their own widths, and whose rows are reachable by keyboard.
 *
 * The row is not a click handler: the first cell is a real link to the row's route, which also
 * gives middle-click and open-in-new-tab. A control inside the row calls `stopPropagation` on
 * its own behalf.
 */

import type { ReactNode } from 'react';
import styles from './DataTable.module.css';

export interface Column<Row> {
  key: string;
  label: string;
  sortable?: boolean;
  numeric?: boolean;
  width?: string;
  /** The header exists for layout but says nothing; announced to a screen reader only. */
  labelHidden?: boolean;
  render: (row: Row) => ReactNode;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export interface DataTableProps<Row> {
  caption: string;
  columns: ReadonlyArray<Column<Row>>;
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  rowInactive?: (row: Row) => boolean;
  sort?: SortState;
  onSortChange?: (key: string) => void;
  minWidth?: number;
  empty?: ReactNode;
}

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  rowInactive,
  sort,
  onSortChange,
  minWidth = 1080,
  empty,
}: DataTableProps<Row>) {
  if (rows.length === 0 && empty) {
    return <div className={styles.wrap}>{empty}</div>;
  }

  return (
    <div className={styles.wrap}>
      <table className={styles.table} style={{ minWidth }}>
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort?.key === column.key;
              const ariaSort = active
                ? sort.dir === 'asc'
                  ? ('ascending' as const)
                  : ('descending' as const)
                : undefined;
              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={ariaSort}
                  style={column.width ? { width: column.width } : undefined}
                  className={[
                    styles.th,
                    column.numeric ? styles.numeric : undefined,
                    column.sortable ? styles.sortable : undefined,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {column.sortable && onSortChange ? (
                    <button
                      type="button"
                      className={styles.sortButton}
                      onClick={() => onSortChange(column.key)}
                    >
                      {column.label}
                      <span className={styles.sortMark} aria-hidden="true">
                        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '▼'}
                      </span>
                    </button>
                  ) : column.labelHidden ? (
                    <span className="visually-hidden">{column.label}</span>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={[styles.rowHover, rowInactive?.(row) ? styles.inactive : undefined]
                .filter(Boolean)
                .join(' ')}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={[styles.td, column.numeric ? styles.numeric : undefined]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
